import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { useQuery } from "@tanstack/react-query";
import { apiGetMarketPlaceNoteValidation } from "@lib/api/marketPlace";
import { SharedContextProps } from "@lib/types";
import { MarketPlaceNoteValidationDrawer } from "@lib/components/PageComponents/MarketPlaceNoteValidation";
import { MarketPlaceNoteValidation } from "@lib/types/NoteValidation";

const SharedMarkePlaceNoteValidation: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  pageIsActive = true,
  withoutContainer = false,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] =
    useState<MarketPlaceNoteValidation | null>(null);

  const handleSelectItem = (item: MarketPlaceNoteValidation) => {
    setSelectedItem(item);
  };

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const noteValidationQuery = useQuery({
    queryKey: ["market-place-note-validation", params],
    queryFn: () => apiGetMarketPlaceNoteValidation(params),
    enabled: !!params && pageIsActive,
  });

  const { data, isLoading, isFetching } = noteValidationQuery;
  const apiData = data?.data?.data;

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        navigate,
        apiData,
        params,
        messages,
        handleSelectItem,
      }),
    [
      apiData,
      handleSelectItem,
      params,
      messages,
      context,
      detailContext,
      navigate,
    ],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params?.filter, columns]);

  const title =
    customTitle || (messages["sidebar.marketPlace.tradeAdvises"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={noteValidationQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
          minusHeight={minusHeight}
        />
      </AppsContent>

      <MarketPlaceNoteValidationDrawer
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </AppsContainer>
  );
};

export default SharedMarkePlaceNoteValidation;
